import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { Json } from "@/integrations/supabase/types";

export interface UserPreferences {
  id?: string;
  tone: "professional" | "casual" | "bold" | "custom";
  emoji_density: "none" | "minimal" | "moderate" | "heavy";
  cta_style: "soft" | "direct" | "question";
  hashtag_count: number;
  brand_voice: string | null;
  keywords_include: string[];
  keywords_avoid: string[];
  platform_rules: Record<string, unknown>;
}

const DEFAULT_PREFERENCES: UserPreferences = {
  tone: "professional",
  emoji_density: "minimal",
  cta_style: "soft",
  hashtag_count: 3,
  brand_voice: null,
  keywords_include: [],
  keywords_avoid: [],
  platform_rules: {},
};

export const usePreferences = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_PREFERENCES);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user) {
      fetchPreferences();
    } else {
      setPreferences(DEFAULT_PREFERENCES);
      setIsLoading(false);
    }
  }, [user]);

  const fetchPreferences = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from("user_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error && error.code !== "PGRST116") {
        console.error("Error fetching preferences:", error);
      }

      if (data) {
        const platformRules = data.platform_rules;
        setPreferences({
          id: data.id,
          tone: data.tone as UserPreferences["tone"],
          emoji_density: data.emoji_density as UserPreferences["emoji_density"],
          cta_style: data.cta_style as UserPreferences["cta_style"],
          hashtag_count: data.hashtag_count,
          brand_voice: data.brand_voice,
          keywords_include: data.keywords_include || [],
          keywords_avoid: data.keywords_avoid || [],
          platform_rules: (typeof platformRules === "object" && platformRules !== null && !Array.isArray(platformRules)) 
            ? (platformRules as Record<string, unknown>) 
            : {},
        });
      }
    } catch (error) {
      console.error("Error fetching preferences:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const savePreferences = async (newPreferences: Partial<UserPreferences>) => {
    if (!user) return false;

    setIsSaving(true);
    try {
      const updatedPrefs = { ...preferences, ...newPreferences };
      
      // Check if preferences exist
      const { data: existingData } = await supabase
        .from("user_preferences")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      const dataToSave = {
        tone: updatedPrefs.tone,
        emoji_density: updatedPrefs.emoji_density,
        cta_style: updatedPrefs.cta_style,
        hashtag_count: updatedPrefs.hashtag_count,
        brand_voice: updatedPrefs.brand_voice,
        keywords_include: updatedPrefs.keywords_include,
        keywords_avoid: updatedPrefs.keywords_avoid,
        platform_rules: updatedPrefs.platform_rules as Json,
      };

      if (existingData) {
        // Update existing
        const { error } = await supabase
          .from("user_preferences")
          .update(dataToSave)
          .eq("user_id", user.id);
        
        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from("user_preferences")
          .insert({
            user_id: user.id,
            ...dataToSave,
          });
        
        if (error) throw error;
      }

      setPreferences(updatedPrefs);
      return true;
    } catch (error) {
      console.error("Error saving preferences:", error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    preferences,
    isLoading,
    isSaving,
    savePreferences,
    refreshPreferences: fetchPreferences,
  };
};
